import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Grid, Label, Menu, Popup, Segment } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import CustomIcon from '~/components/CustomIcon'
import InfoPopup from '~/components/InfoPopup'
import { useAuth } from '~/routes/components/AuthProvider'
import { formatNumberWithoutFractions, getPrefixData } from '~/utils/helpers'
import {
  StyledHeader,
  StyledColumnValue,
  TitleWrapper,
  StyledContainer,
  StyledMenuWrapper,
  NoTradingSlotsWrapper
} from '../style'
import TermsModal from '~/components/TermsModal'

interface TradingSlotCardProps {
  tradingSlotCard: any
  settingsData: any
  url: {
    show: string,
    results: string,
    offer: string
  }
  prefix: string
}

const SlotCard: React.FunctionComponent<TradingSlotCardProps> = (props) => {
  const { t } = useTranslation()
  const { settingsData, url: { show, results, offer }, prefix } = props
  const [tradingSlotCard, setTradingSlotCard] = useState(null)
  const { systemData: { amountUnit } } = useAuth()
  const { firstColor } = useContext(ThemeContext)
  const [acceptTermsModal, setAcceptTermsModal] = useState(false)
  const [link, setLink] = useState('')

  const prefixData = getPrefixData(prefix, amountUnit)

  useEffect(() => {
    setTradingSlotCard(props.tradingSlotCard)
  }, [props.tradingSlotCard, settingsData])

  function renderDate(value) {
    const dateFrom = new Date(value.dateFrom);
    const dateTo = new Date(value.dateTo);
    // get Day name and year
    const dateWithDayAndYearTo = dayjs(dateTo).format('dddd, D MMMM YYYY HH:mm');
    const dateWithDayAndYear = dayjs(dateFrom).format('dddd, D MMMM YYYY HH:mm');

    return <StyledColumnValue>{`${dateWithDayAndYear} - ${dateWithDayAndYearTo}`}</StyledColumnValue>
  }

  function renderEmptyTradingSlot() {
    return <NoTradingSlotsWrapper attached>
      <div style={{ textAlign: 'center' }}>
        <CustomIcon className='icon-seeding' style={{ fontSize: '4em', cursor: 'default' }} />
        <div style={{ marginTop: 10, color: firstColor }}>{t('No {{title}}', { title: tradingSlotCard?.title || '' })}</div>
      </div>
    </NoTradingSlotsWrapper>
  }

  function getTriggerIcon(link: string, iconName, disabled = false) {
    if (!tradingSlotCard?.termsAccepted && settingsData?.tradingRoom && settingsData?.terms?.length > 0) {
      return <CustomIcon
        className='action-icon'
        name={iconName}
        color={disabled ? 'grey' : firstColor}
        disabled={disabled}
        onClick={() => {
          setAcceptTermsModal(true)
          setLink(link)
        }}
      />
    } else {
      return <Link to={link}>
        <CustomIcon
          color={disabled ? 'grey' : firstColor}
          disabled={disabled}
          size={'large'}
          className='action-icon'
          name={iconName}
        />
      </Link>
    }
  }

  const tradingSlotDataIsEmpty = tradingSlotCard && Object.keys(tradingSlotCard).length === 1

  return <StyledContainer>
    <TermsModal
      openTermsModal={acceptTermsModal}
      link={link}
      prefix={prefix}
      handleClose={() => {
        setAcceptTermsModal(false)
        setLink('')
      }}
      tradingSlotId={tradingSlotCard?.id}
      settingsData={settingsData}
    />
    <StyledHeader attached='top' block>
      <div className='title'>
        {tradingSlotCard?.title}
      </div>
      {!tradingSlotDataIsEmpty && <div style={{ display: 'flex', alignItems: 'baseline' }}>
        <Label as='a' style={{ background: firstColor, color: 'white', cursor: 'default', marginLeft: 0 }} ribbon='right'>
          {tradingSlotCard?.statusLabel}
        </Label>
      </div>}
    </StyledHeader>
    {tradingSlotDataIsEmpty ? renderEmptyTradingSlot() : <Segment style={{ position: 'relative', height: 270, maxHeight: 270 }} attached>
      <Grid columns={2}>
        <Grid.Row columns={2}>
          <Grid.Column>
            <div>{t('Trading slot ID')}</div>
            <StyledColumnValue>{tradingSlotCard?.uniqueId}</StyledColumnValue>
          </Grid.Column>
          <Grid.Column>
            {t('Trading volume type')}
            <StyledColumnValue>{tradingSlotCard?.tradingVolumeTypeName}</StyledColumnValue>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={2}>
          <Grid.Column>
            <TitleWrapper>
              <div>{t('Offered amount (in {{amountUnit}})', { amountUnit })}</div>
              <InfoPopup
                content={t('Total of amounts which you have offered throughout all trading months within this trading slot.')}
                styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 5 }}
              />
            </TitleWrapper>
            <StyledColumnValue>{formatNumberWithoutFractions(tradingSlotCard?.offeredAmount) || 0}</StyledColumnValue>
          </Grid.Column>
          <Grid.Column>
            <TitleWrapper>
              <div>{prefixData.confirm}</div>
              <InfoPopup
                content={t('The amount your dairy has actually confirmed (at most corresponding with the total of amounts you have offered).')}
                styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 5 }}
              />
            </TitleWrapper>
            <StyledColumnValue>{formatNumberWithoutFractions(tradingSlotCard?.confirmedAmount) || 0}</StyledColumnValue>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={1}>
          <Grid.Column>
            <TitleWrapper>
              <div>{t('Start - End (offer period)')}</div>
              <InfoPopup
                content={t('Period of time in which you can offer trading amounts.')}
                styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 5 }}
              />
            </TitleWrapper>
            <StyledColumnValue>{tradingSlotCard ? renderDate(tradingSlotCard) : null}</StyledColumnValue>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <StyledMenuWrapper>
        <Menu compact className='actions' icon>
          <Menu.Item>
            <Popup
              content={t('Show more')}
              trigger={getTriggerIcon(show, 'eye')
              }
            />
          </Menu.Item>
          <Menu.Item>
            <Popup
              content={t('Results')}
              trigger={getTriggerIcon(
                results,
                'clipboard outline',
                ['active', 'completed', 'examination'].find(status => tradingSlotCard?.status === status) === undefined
              )}
            />
          </Menu.Item>
          <Menu.Item>
            <Popup
              content={t('Offer amount')}
              trigger={getTriggerIcon(offer, 'reply all', tradingSlotCard?.status !== 'active')}
            /></Menu.Item>
          {/*  <Menu.Item>
          <Popup
            content={t('Results')}
            trigger={<CustomIcon className='action-icon' name='file alternate'/>}
          /></Menu.Item> */}
        </Menu>
      </StyledMenuWrapper>
    </Segment>
    }
  </StyledContainer>
}
export default SlotCard
